import { Injectable } from '@angular/core';
import { EExecutionType } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { IBaseDeliverableAnalyticsApi, IPlannedExecutionApi } from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode } from '@interfaces/campaign-analytics.interface';
import { TranslateService } from '@ngx-translate/core';
import { DATE_FORMAT, DATE_TIME_FORMAT, DateUtilitiesService } from '@utilities/date-utilities.service';
import { DeliverableUtilitiesService } from '@utilities/deliverable-utilities.service';
import { getTimeFrameLabel } from '@utilities/execution-time-frame.util';
import { isValid } from 'date-fns';
import { CampaignDetailsUndesignatedExecutionOrderService } from '../campaign-details-undesignated-execution-order.service';
import {
  ICampaignDetailsDeliverablesFilterOption,
  ICampaignDetailsDeliverablesFilterState,
  ICampaignDetailsDeliverablesGroupedChildRow,
  ICampaignDetailsDeliverablesGroupedParentRow,
  ICampaignDetailsDeliverablesStatusOption,
  ICampaignDetailsDeliverablesTableRow,
  IDateMetadata,
  IDeliverablesSourceCta,
  IRowStatusConfig,
  TCampaignDetailsDeliverablesGroupedRow,
  TCampaignDetailsDeliverablesRowStatusKey,
} from './campaign-details-deliverables.interface';

interface IBuildTableRowParams {
  analyticsEntry: IBaseDeliverableAnalyticsApi;
  analyticsNode: IAnalyticsNode;
  campaignIconById?: Map<string, string | null | undefined>;
  campaignNameById?: Map<string, string>;
  deliverableType: EDeliverableType;
  sourceCta: IDeliverablesSourceCta;
  translate: TranslateService;
  undesignatedExecutionOrder: number | null;
}

@Injectable()
export class CampaignDetailsDeliverablesService {
  private readonly statusOrder: TCampaignDetailsDeliverablesRowStatusKey[] = ['failed', 'problem', 'processing', 'ok'];

  private readonly statusBadgeOrder: EAnalyticsStatus[] = [
    EAnalyticsStatus.failed,
    EAnalyticsStatus.problem,
    EAnalyticsStatus.processing,
    EAnalyticsStatus.ok,
  ];

  constructor(
    private readonly dateUtils: DateUtilitiesService,
    private readonly deliverableUtilities: DeliverableUtilitiesService,
    private readonly undesignatedExecutionOrderService: CampaignDetailsUndesignatedExecutionOrderService,
  ) {}

  public buildFilterOptions(
    rows: ICampaignDetailsDeliverablesTableRow[],
    translate: TranslateService,
  ): {
    ctaTemplateNames: ICampaignDetailsDeliverablesFilterOption<string>[];
    deliverableTypes: ICampaignDetailsDeliverablesFilterOption<EDeliverableType>[];
    matchingTypes: ICampaignDetailsDeliverablesFilterOption<EMatchingType>[];
  } {
    const ctaTemplateNames = Array.from(new Set(rows.map((row) => row.ctaTemplateName)))
      .map((value) => ({ label: value, value }))
      .sort((left, right) => left.label.localeCompare(right.label));

    const deliverableTypes = Array.from(new Set(rows.map((row) => row.deliverableType)))
      .map((type) => ({ label: translate.instant(`shared.deliverableTypes.${type}`) as string, value: type }))
      .sort((left, right) => left.label.localeCompare(right.label));

    const presentMatchingTypes = new Set(rows.map((row) => row.matchingType));

    const matchingTypes = [
      EMatchingType.bonus,
      EMatchingType.matched,
      EMatchingType.missing,
      EMatchingType.planned,
      EMatchingType.unrelated,
    ].reduce<ICampaignDetailsDeliverablesFilterOption<EMatchingType>[]>((acc, matchingType) => {
      if (presentMatchingTypes.has(matchingType)) {
        acc.push({ label: this.translateMatchingType(matchingType, translate), value: matchingType });
      }

      return acc;
    }, []);

    return {
      ctaTemplateNames,
      deliverableTypes,
      matchingTypes,
    };
  }

  /**
   * `campaignNameById` is only passed by the multi-campaign context (creator-details' Deliverables
   * tab, where `analyticsNodes` spans several campaigns for one creator) to populate each row's
   * `campaignId`/`campaignName` and to defensively namespace row/group ids by campaign - see
   * `buildTableRow`/`buildExecutionAwareGroupKey`. Campaign-details' own call site omits it and is
   * completely unaffected.
   */
  public buildRows(
    analyticsNodes: IAnalyticsNode[] | null,
    translate: TranslateService,
    campaignNameById?: Map<string, string>,
    campaignIconById?: Map<string, string | null | undefined>,
  ): ICampaignDetailsDeliverablesTableRow[] {
    if (!analyticsNodes?.length) {
      return [];
    }

    const fallbackUndesignatedOrderMap = this.undesignatedExecutionOrderService.buildOrderMap(analyticsNodes);
    const rows: ICampaignDetailsDeliverablesTableRow[] = [];

    for (const analyticsNode of analyticsNodes) {
      const sourceCtas = this.getSourceCtas(analyticsNode);

      for (const sourceCta of sourceCtas) {
        for (const config of this.deliverableUtilities.getDeliverableCollectionConfigs(sourceCta.ctaAnalytics)) {
          if (this.deliverableUtilities.isTrackingDeliverable(config.type)) {
            continue;
          }

          const analyticsEntries = sourceCta.ctaAnalytics[config.collectionKey] ?? [];
          const resolvedPlannedExecution = sourceCta.plannedExecution ?? analyticsNode.plannedExecution ?? null;
          const undesignatedLookupKey = this.undesignatedExecutionOrderService.getLookupKey(
            analyticsNode.id,
            sourceCta.ctaId,
            resolvedPlannedExecution,
          );
          let enrichedUndesignatedExecutionOrder: number | null = null;
          let fallbackUndesignatedExecutionOrder: number | null = null;

          if (undesignatedLookupKey) {
            enrichedUndesignatedExecutionOrder =
              analyticsNode.undesignatedExecutionOrdersByLookupKey?.[undesignatedLookupKey] ?? null;
            fallbackUndesignatedExecutionOrder = fallbackUndesignatedOrderMap.get(undesignatedLookupKey) ?? null;
          }

          const undesignatedExecutionOrder = enrichedUndesignatedExecutionOrder ?? fallbackUndesignatedExecutionOrder;

          for (const analyticsEntry of analyticsEntries) {
            rows.push(
              this.buildTableRow({
                analyticsEntry,
                analyticsNode,
                campaignIconById,
                campaignNameById,
                deliverableType: config.type,
                sourceCta,
                translate,
                undesignatedExecutionOrder,
              }),
            );
          }
        }
      }
    }

    return rows;
  }

  public buildStatusOptions(
    rows: ICampaignDetailsDeliverablesTableRow[],
    selectedStatuses: Set<EAnalyticsStatus>,
  ): ICampaignDetailsDeliverablesStatusOption<EAnalyticsStatus>[] {
    return this.statusBadgeOrder.map((status, index): ICampaignDetailsDeliverablesStatusOption<EAnalyticsStatus> => {
      const count = rows.filter((row) => row.statusFilterValue === status).length;

      return {
        colorScheme: 'deliverable',
        count,
        isDisabled: count === 0 && !selectedStatuses.has(status),
        key: status,
        sortOrder: index,
        status,
      };
    });
  }

  public buildGroupedRows(rows: ICampaignDetailsDeliverablesTableRow[]): TCampaignDetailsDeliverablesGroupedRow[] {
    const groupedRowMap = new Map<string, TCampaignDetailsDeliverablesGroupedRow>();

    for (const row of rows) {
      const groupId = this.buildExecutionAwareGroupKey(row);
      const existingGroup = groupedRowMap.get(groupId);

      if (!existingGroup) {
        const parent: ICampaignDetailsDeliverablesGroupedParentRow = {
          analyticsNodeId: row.analyticsNodeId,
          campaignIcon: row.campaignIcon,
          campaignId: row.campaignId,
          campaignName: row.campaignName,
          creatorIcon: row.creatorIcon,
          creatorId: row.creatorId,
          creatorName: row.creatorName,
          ctaExecutionId: row.ctaExecutionId,
          ctaTemplateId: row.ctaTemplateId,
          ctaTemplateName: row.ctaTemplateName,
          dateLabel: row.dateLabel,
          dateSortValue: row.dateSortValue,
          deliverablesCount: 0,
          executionGroupingKey: this.buildExecutionGroupingKey(row),
          id: groupId,
          isPlannedUndesignatedExecution: row.isPlannedUndesignatedExecution,
          matchingType: row.matchingType,
          matchingTypeLabel: row.matchingTypeLabel,
          plannedUndesignatedOrder: row.executionOrder ?? row.executionCountTarget ?? null,
          platformFeature: row.platformFeature,
        };

        groupedRowMap.set(groupId, {
          children: [],
          id: groupId,
          isExpanded: true,
          parent,
        });
      }

      const groupedRow = groupedRowMap.get(groupId);

      if (!groupedRow) {
        continue;
      }

      const childRow: ICampaignDetailsDeliverablesGroupedChildRow = {
        id: row.id,
        rowStatus: row.rowStatus,
        rowStatusColorScheme: row.rowStatusColorScheme,
        sourceRow: row,
      };

      groupedRow.children.push(childRow);
      groupedRow.parent.deliverablesCount = groupedRow.children.length;
    }

    return Array.from(groupedRowMap.values());
  }

  /** The group filter works on names; a deep link names groups by id, so take the names their rows carry. */
  public getCtaTemplateNames(rows: ICampaignDetailsDeliverablesTableRow[], ctaTemplateIds?: Set<string>): Set<string> {
    if (!ctaTemplateIds?.size) {
      return new Set<string>();
    }

    return new Set(
      rows
        .filter((row) => row.ctaTemplateId && ctaTemplateIds.has(row.ctaTemplateId))
        .map((row) => row.ctaTemplateName),
    );
  }

  public createInitialFilterState(): ICampaignDetailsDeliverablesFilterState {
    return {
      ctaTemplateNames: new Set<string>(),
      deliverableTypes: new Set<EDeliverableType>(),
      matchingTypes: new Set<EMatchingType>(),
      statuses: new Set<EAnalyticsStatus>(),
    };
  }

  public filterRows(
    rows: ICampaignDetailsDeliverablesTableRow[],
    filterState: ICampaignDetailsDeliverablesFilterState,
  ): ICampaignDetailsDeliverablesTableRow[] {
    return rows.filter((row) => {
      if (filterState.ctaTemplateNames.size > 0 && !filterState.ctaTemplateNames.has(row.ctaTemplateName)) {
        return false;
      }

      if (filterState.matchingTypes.size > 0 && !filterState.matchingTypes.has(row.matchingType)) {
        return false;
      }

      if (filterState.deliverableTypes.size > 0 && !filterState.deliverableTypes.has(row.deliverableType)) {
        return false;
      }

      if (filterState.statuses.size > 0 && !filterState.statuses.has(row.statusFilterValue)) {
        return false;
      }

      return true;
    });
  }

  private buildTableRow({
    analyticsEntry,
    analyticsNode,
    campaignIconById,
    campaignNameById,
    deliverableType,
    sourceCta,
    translate,
    undesignatedExecutionOrder,
  }: IBuildTableRowParams): ICampaignDetailsDeliverablesTableRow {
    const dateMetadata = this.getDateMetadata(
      analyticsNode,
      sourceCta.plannedExecution,
      translate,
      undesignatedExecutionOrder,
    );
    const rowStatusConfig = this.getRowStatusConfig(analyticsEntry);
    // Only set when building rows for a multi-campaign context (campaignNameById is passed) -
    // campaign-details' own single-campaign call site leaves both undefined.
    const campaignId = campaignNameById ? analyticsNode.campaignId : undefined;
    const campaignName = campaignId ? (campaignNameById?.get(campaignId) ?? '') : undefined;
    const campaignIcon = campaignId ? (campaignIconById?.get(campaignId) ?? null) : undefined;
    // Defensive campaignId namespacing: analyticsNode.id / sourceCta.ctaId / deliverableId are
    // never guaranteed unique ACROSS campaigns (they're DB UUIDs scoped to a campaign's own CTA
    // templates and content, which happens to make collisions extremely unlikely in practice, but
    // this row id is being merged across campaigns for the first time here) - prefixing with
    // campaignId when known costs nothing and removes the concern entirely.
    const rowIdPrefix = campaignId ? `${campaignId}_` : '';

    return {
      analyticsNodeId: analyticsNode.id,
      campaignIcon,
      campaignId,
      campaignName,
      creatorIcon: analyticsNode.creator.icon,
      creatorId: analyticsNode.creatorId,
      creatorName: analyticsNode.creator.name,
      ctaExecutionId: sourceCta.plannedExecution?.ctaExecutionId ?? null,
      ctaTemplateId: sourceCta.ctaId,
      ctaTemplateName: sourceCta.ctaName,
      dateLabel: dateMetadata.label,
      dateSortValue: dateMetadata.sortValue,
      deliverableId: analyticsEntry.deliverableId,
      deliverableName: analyticsEntry.name,
      deliverableType,
      deliverableTypeLabel: translate.instant(`shared.deliverableTypes.${deliverableType}`),
      executionCountTarget: dateMetadata.executionCountTarget,
      executionOrder: dateMetadata.executionOrder,
      executionType: dateMetadata.executionType,
      id: `${rowIdPrefix}${analyticsNode.id}_${sourceCta.ctaId ?? 'no-cta'}_${analyticsEntry.deliverableId}`,
      isDateOnlyExecution: dateMetadata.isDateOnlyExecution,
      isPlannedUndesignatedExecution: this.isPlannedUndesignatedExecution(dateMetadata),
      matchingType: sourceCta.matchingType,
      matchingTypeLabel: this.translateMatchingType(sourceCta.matchingType, translate),
      normalizedStatusKey: rowStatusConfig.key,
      platformFeature: analyticsNode.platformFeature,
      rowStatus: rowStatusConfig.status,
      rowStatusColorScheme: rowStatusConfig.colorScheme,
      statusFilterValue: rowStatusConfig.analyticsStatus,
      statusSortValue: rowStatusConfig.sortOrder,
    };
  }

  private buildExecutionAwareGroupKey(row: ICampaignDetailsDeliverablesTableRow): string {
    const ctaTemplateKey = row.ctaTemplateId ?? 'no-cta';
    const executionGroupingKey = this.buildExecutionGroupingKey(row);
    // Defensive campaignId namespacing (see buildTableRow's rowIdPrefix comment) - row.creatorId
    // is the SAME value for every row in the multi-campaign context (one creator, many campaigns),
    // so this key would otherwise rely entirely on ctaTemplateId/executionGroupingKey differing
    // across campaigns to avoid merging two different campaigns' deliverables into one group.
    const campaignKey = row.campaignId ? `${row.campaignId}::` : '';

    return `${campaignKey}${row.creatorId}::${ctaTemplateKey}::${row.matchingType}::${executionGroupingKey}::${row.dateSortValue}`;
  }

  private buildExecutionGroupingKey(row: ICampaignDetailsDeliverablesTableRow): string {
    if (row.isPlannedUndesignatedExecution) {
      const plannedUndesignatedOrder = row.executionOrder ?? row.executionCountTarget ?? 0;

      return `undesignated-planned-${plannedUndesignatedOrder}`;
    }

    // A time_frame with count N has N planned nodes sharing one ctaExecutionId - keep them apart.
    if (row.executionType === EExecutionType.timeFrame && row.executionOrder !== null) {
      return `time-frame-planned-${row.ctaExecutionId ?? row.analyticsNodeId}-${row.executionOrder}`;
    }

    return `execution-${row.ctaExecutionId ?? row.analyticsNodeId}`;
  }

  private getDateMetadata(
    analyticsNode: IAnalyticsNode,
    plannedExecution: IPlannedExecutionApi | null,
    translate: TranslateService,
    undesignatedExecutionOrder: number | null,
  ): IDateMetadata {
    const executedAt =
      analyticsNode.content?.socialContent?.executionTime ?? this.getValidExecutionDate(analyticsNode.executionTime);

    if (executedAt) {
      return this.createExecutedDateMetadata(analyticsNode, executedAt, plannedExecution);
    }

    const resolvedPlannedExecution = plannedExecution ?? analyticsNode.plannedExecution ?? null;

    if (!resolvedPlannedExecution) {
      return this.createEmptyDateMetadata(
        analyticsNode.plannedExecution?.executionType ?? null,
        analyticsNode.isDateOnlyExecution === true,
      );
    }

    if (resolvedPlannedExecution.executionType === EExecutionType.always) {
      return this.createStaticDateMetadata(
        resolvedPlannedExecution,
        translate.instant('pages.campaignDetails.deliverables.table.date.always'),
        null,
      );
    }

    if (resolvedPlannedExecution.executionType === EExecutionType.timeFrame) {
      return this.createTimeFrameDateMetadata(resolvedPlannedExecution, translate, undesignatedExecutionOrder);
    }

    if (resolvedPlannedExecution.executionType === EExecutionType.undesignated) {
      const executionOrder = undesignatedExecutionOrder ?? resolvedPlannedExecution.executionCountTarget ?? 0;

      return this.createStaticDateMetadata(
        resolvedPlannedExecution,
        translate.instant('pages.campaignDetails.deliverables.table.date.anyTime', {
          executionOrder,
        }),
        executionOrder,
      );
    }

    return this.createPlannedDateMetadata(resolvedPlannedExecution);
  }

  private createEmptyDateMetadata(executionType: EExecutionType | null, isDateOnlyExecution: boolean): IDateMetadata {
    return {
      executionCountTarget: null,
      executionOrder: null,
      executionType,
      isDateOnlyExecution,
      label: '',
      sortValue: 0,
    };
  }

  private createExecutedDateMetadata(
    analyticsNode: IAnalyticsNode,
    executedAt: string,
    plannedExecution: IPlannedExecutionApi | null,
  ): IDateMetadata {
    const executedDate = new Date(executedAt);
    const isDateOnlyExecution = analyticsNode.isDateOnlyExecution === true;

    return {
      executionCountTarget: plannedExecution?.executionCountTarget ?? null,
      executionOrder: null,
      executionType: plannedExecution?.executionType ?? analyticsNode.plannedExecution?.executionType ?? null,
      isDateOnlyExecution,
      label: this.dateUtils.formatDate(executedDate, isDateOnlyExecution ? DATE_FORMAT : DATE_TIME_FORMAT),
      sortValue: executedDate.getTime(),
    };
  }

  private createPlannedDateMetadata(plannedExecution: IPlannedExecutionApi): IDateMetadata {
    const plannedDate = this.dateUtils.parseTimeFromApi(
      plannedExecution.plannedStartDate ?? null,
      plannedExecution.plannedStartTime ?? null,
    );
    const isDateOnlyExecution = !plannedExecution.plannedStartTime;

    if (!plannedDate || !isValid(plannedDate)) {
      return this.createEmptyDateMetadata(plannedExecution.executionType, isDateOnlyExecution);
    }

    return {
      executionCountTarget: plannedExecution.executionCountTarget ?? null,
      executionOrder: null,
      executionType: plannedExecution.executionType,
      isDateOnlyExecution,
      label: this.dateUtils.formatDate(plannedDate, isDateOnlyExecution ? DATE_FORMAT : DATE_TIME_FORMAT),
      sortValue: plannedDate.getTime(),
    };
  }

  private createTimeFrameDateMetadata(
    plannedExecution: IPlannedExecutionApi,
    translate: TranslateService,
    executionOrder: number | null,
  ): IDateMetadata {
    // Without a window the order and target still count - the N planned nodes of a count-N time frame
    // share one ctaExecutionId and would merge into one row group.
    return {
      executionCountTarget: plannedExecution.executionCountTarget ?? null,
      executionOrder,
      executionType: plannedExecution.executionType,
      isDateOnlyExecution: false,
      label: getTimeFrameLabel(plannedExecution, executionOrder, translate, this.dateUtils),
      sortValue: plannedExecution.plannedStart
        ? this.dateUtils.parseDateTimeFromApi(plannedExecution.plannedStart).getTime()
        : 0,
    };
  }

  private createStaticDateMetadata(
    plannedExecution: IPlannedExecutionApi,
    label: string,
    executionOrder: number | null,
  ): IDateMetadata {
    return {
      executionCountTarget: plannedExecution.executionCountTarget ?? null,
      executionOrder,
      executionType: plannedExecution.executionType,
      isDateOnlyExecution: true,
      label,
      sortValue: 0,
    };
  }

  private getRowStatusConfig(analyticsEntry: IBaseDeliverableAnalyticsApi): IRowStatusConfig {
    const resolvedStatus = analyticsEntry.statusManual ?? analyticsEntry.status ?? EAnalyticsStatus.processing;
    const colorScheme = 'deliverable';

    switch (resolvedStatus) {
      case EAnalyticsStatus.failed:
        return {
          analyticsStatus: EAnalyticsStatus.failed,
          colorScheme,
          key: 'failed',
          sortOrder: this.statusOrder.indexOf('failed'),
          status: EAnalyticsStatus.failed,
        };
      case EAnalyticsStatus.ok:
        return {
          analyticsStatus: EAnalyticsStatus.ok,
          colorScheme,
          key: 'ok',
          sortOrder: this.statusOrder.indexOf('ok'),
          status: EAnalyticsStatus.ok,
        };
      case EAnalyticsStatus.problem:
        return {
          analyticsStatus: EAnalyticsStatus.problem,
          colorScheme,
          key: 'problem',
          sortOrder: this.statusOrder.indexOf('problem'),
          status: EAnalyticsStatus.problem,
        };
      default:
        return {
          analyticsStatus: EAnalyticsStatus.processing,
          colorScheme,
          key: 'processing',
          sortOrder: this.statusOrder.indexOf('processing'),
          status: EAnalyticsStatus.processing,
        };
    }
  }

  private getSourceCtas(analyticsNode: IAnalyticsNode): IDeliverablesSourceCta[] {
    const contentCtas = Array.from(analyticsNode.content?.ctas?.values() ?? []).map((ctaResult) => ({
      ctaAnalytics: ctaResult.ctaAnalytics,
      ctaId: ctaResult.ctaId,
      ctaName: ctaResult.name,
      matchingType: ctaResult.matchingType,
      plannedExecution: ctaResult.plannedExecution ?? analyticsNode.plannedExecution ?? null,
    }));

    if (contentCtas.length > 0) {
      return contentCtas;
    }

    if (!analyticsNode.emptyCtaAnalytics || !analyticsNode.nonExecutedCtaName) {
      return [];
    }

    return [
      {
        ctaAnalytics: analyticsNode.emptyCtaAnalytics,
        ctaId: analyticsNode.nonExecutedCtaId,
        ctaName: analyticsNode.nonExecutedCtaName,
        matchingType: analyticsNode.executionStatus,
        plannedExecution: analyticsNode.plannedExecution ?? null,
      },
    ];
  }

  private isPlannedUndesignatedExecution(dateMetadata: IDateMetadata): boolean {
    if (dateMetadata.executionType !== EExecutionType.undesignated) {
      return false;
    }

    if (dateMetadata.executionOrder === null && dateMetadata.executionCountTarget === null) {
      return false;
    }

    if (dateMetadata.sortValue > 0) {
      return false;
    }

    return true;
  }

  private translateMatchingType(matchingType: EMatchingType, translate: TranslateService): string {
    return translate.instant(`pages.campaignDetails.ctaAnalytics.details.deliverables.matching.${matchingType}`);
  }

  private getValidExecutionDate(executionTime: string | null): string | null {
    if (!executionTime) {
      return null;
    }

    const parsedDate = new Date(executionTime);

    if (!isValid(parsedDate)) {
      return null;
    }

    return executionTime;
  }
}
